// POS step two — /pos/cart.
//
// Step one only counted products. Here the cashier says which batch each unit
// comes from (only when a product has more than one on the event), switches
// the event's promos on, sees the same totals the invoice will carry, and
// pays. The database recomputes everything; this page's arithmetic
// (src/lib/pricing.ts) mirrors it so what is shown is what is charged.

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Receipt, Gift, Percent, AlertTriangle, CheckCircle2, User, ShoppingCart } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Toggle } from '@/components/ui/Toggle';
import { EmptyState } from '@/components/ui/EmptyState';
import { CustomerPicker } from '@/components/customers/CustomerPicker';
import { CustomerForm } from '@/components/customers/CustomerForm';
import { DiscountToggle } from '@/components/pos/DiscountToggle';
import { FulfillmentToggle } from '@/components/pos/FulfillmentToggle';
import { Checkout } from '@/components/pos/Checkout';
import { HelpButton } from '@/components/tour/HelpButton';
import { useEvent } from '@/hooks/useEvents';
import { useEventPromos } from '@/hooks/useEventPromos';
import { useEventAllocations } from '@/hooks/useEventAllocations';
import { useDeleteDraft } from '@/hooks/useDrafts';
import { useCart, expandLines, isAssigned, assignedQty, type CartItem } from '@/store/cart';
import { usePosCheckout } from '@/store/posCheckout';
import { usePosShiftStore } from '@/store/posShift';
import { calculateTotals, applyPromos, resolveAutoPromos, type PromoDef } from '@/lib/pricing';
import { cn, formatIDR, formatNumber, formatDate } from '@/lib/format';
import type { EventPromo, PaymentTerms } from '@/types/domain';

const toPromoDef = (p: EventPromo): PromoDef => ({ id: p.id, kind: p.kind, name: p.name, config: p.config, items: p.items });

export default function POSCartReview() {
  const navigate = useNavigate();
  const shift = usePosShiftStore();
  const { data: activeEvent } = useEvent(shift.eventId);
  const { data: promos } = useEventPromos(shift.eventId);
  const { data: allocations } = useEventAllocations(shift.eventId || null);

  const items = useCart((s) => s.items);
  const setLots = useCart((s) => s.setLots);
  const autoLots = useCart((s) => s.autoLots);
  const updateQty = useCart((s) => s.updateQty);
  const removeItem = useCart((s) => s.removeItem);
  const refreshLots = useCart((s) => s.refreshLots);

  const co = usePosCheckout();
  const deleteDraft = useDeleteDraft();

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [customerFormOpen, setCustomerFormOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live stock may have moved since step one (another till sold from a lot).
  const refresh = useCallback(() => {
    if (!allocations) return;
    const byProduct = new Map<string, CartItem['lotOptions']>();
    for (const a of allocations) {
      if (a.closedAt) continue;
      const list = byProduct.get(a.productId) ?? [];
      list.push({
        allocationId: a.id,
        lotNumber: a.lotNumber || undefined,
        expiryDate: a.expiryDate,
        unit: a.unit,
        remaining: Math.max(0, a.qtyIn - a.qtySold),
      });
      byProduct.set(a.productId, list);
    }
    for (const list of byProduct.values()) list.sort((x, y) => (x.expiryDate ?? Infinity) - (y.expiryDate ?? Infinity));
    refreshLots(byProduct);
  }, [allocations, refreshLots]);

  const activePromos = useMemo(() => (promos ?? []).filter((p) => p.isActive), [promos]);
  const lines = useMemo(() => expandLines(items), [items]);
  // Buy-N-get-M applies by itself whenever the cart reaches it; bundles and the
  // tiered discount stay the cashier's choice.
  const applied = useMemo(
    () => resolveAutoPromos(lines, activePromos.map(toPromoDef), co.promoIds),
    [lines, activePromos, co.promoIds],
  );

  const totals = useMemo(
    () =>
      calculateTotals({
        items,
        promoLines: lines,
        promos: applied,
        discountType: co.discountType,
        discountValue: co.discountValue,
        taxEnabled: co.taxEnabled,
        taxRate: co.taxRate,
      }),
    [items, lines, applied, co.discountType, co.discountValue, co.taxEnabled, co.taxRate],
  );

  // Per product: promo money off, for the line list.
  const promoByProduct = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of items) {
      let sum = 0;
      for (const l of i.lots) sum += totals.promoByLine.get(l.allocationId) ?? 0;
      m.set(i.productId, sum);
    }
    return m;
  }, [items, totals.promoByLine]);

  const unassigned = items.filter((i) => !isAssigned(i));
  const eventActive = !!activeEvent && activeEvent.status === 'active';
  const canCharge =
    eventActive && items.length > 0 && unassigned.length === 0 && !totals.promoProblem && totals.grandTotal > 0;

  const handleSuccess = useCallback(
    (result: { id: string; invoiceNumber: string; paymentTerms: PaymentTerms }) => {
      setPaymentOpen(false);
      shift.recordTransaction(totals.grandTotal);
      if (co.loadedDraftId && shift.cashierId) {
        deleteDraft.mutateAsync({ id: co.loadedDraftId, cashierId: shift.cashierId }).catch(() => {});
      }
      co.resetSale();
      co.setLastSuccess(result);
      navigate('/pos');
    },
    [shift, totals.grandTotal, co, deleteDraft, navigate],
  );

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <header className="px-4 sm:px-6 lg:px-8 py-2.5 border-b border-line bg-surface flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pos')} leftIcon={<ArrowLeft size={14} />}>
            Produk
          </Button>
          <div className="min-w-0">
            <p className="text-overline uppercase text-ink-subtle font-medium leading-none">Keranjang · langkah 2</p>
            <p className="text-body font-display font-semibold text-ink truncate leading-tight">
              {activeEvent?.name ?? '…'}
              {activeEvent?.code && <code className="ml-1.5 text-caption font-mono text-ink-subtle">{activeEvent.code}</code>}
            </p>
          </div>
          {!eventActive && activeEvent && <Chip variant="default">{activeEvent.status}</Chip>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <HelpButton />
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded bg-canvas border border-line">
            <User size={14} className="text-ink-subtle" />
            <span className="text-small text-ink">{shift.cashierName}</span>
          </div>
        </div>
      </header>

      {items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <EmptyState
            icon={<ShoppingCart size={24} strokeWidth={1.5} />}
            title="Keranjang kosong"
            description="Pilih produk dulu di langkah pertama."
            action={<Button variant="primary" onClick={() => navigate('/pos')}>Ke daftar produk</Button>}
          />
        </div>
      ) : (
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-4">
          {error && (
            <Card padding="md" className="mb-3 border-danger/30 bg-danger/5">
              <p className="text-small text-danger">{error}</p>
            </Card>
          )}
          <div className="grid gap-4 lg:grid-cols-[1fr_380px] items-start">
            {/* ── Lines & lots ── */}
            <div className="space-y-3" data-tour="cart-lines">
              <div className="flex items-center justify-between">
                <h2 className="text-subhead font-display font-semibold text-ink">
                  {formatNumber(items.reduce((s, i) => s + i.qty, 0))} item · {items.length} produk
                </h2>
                <Button variant="ghost" size="sm" onClick={refresh} disabled={!allocations}>
                  Segarkan stok
                </Button>
              </div>
              {unassigned.length > 0 && (
                <p className="text-caption text-warning inline-flex items-center gap-1.5">
                  <AlertTriangle size={13} />
                  {unassigned.length} produk belum lengkap pembagian lotnya — isi jumlah per lot di bawah.
                </p>
              )}
              <ul className="space-y-3">
                {items.map((item) => (
                  <li key={item.lineId}>
                    <LineCard
                      item={item}
                      promoOff={promoByProduct.get(item.productId) ?? 0}
                      onQty={(q) => updateQty(item.lineId, q)}
                      onRemove={() => removeItem(item.lineId)}
                      onLots={(lots) => setLots(item.lineId, lots)}
                      onAutoLots={() => autoLots(item.lineId)}
                    />
                  </li>
                ))}
              </ul>
            </div>

            {/* ── Promos, customer, totals ── */}
            <div className="space-y-4 lg:sticky lg:top-4">
              <Card padding="sm" className="overflow-visible relative z-20">
                <CustomerPicker
                  value={co.customer?.id ?? null}
                  onSelect={(c) => co.setCustomer(c)}
                  onAddNew={() => setCustomerFormOpen(true)}
                  placeholder="Pilih customer (opsional)…"
                  id="review-customer-picker"
                />
              </Card>

              {activePromos.length > 0 && (
                <Card data-tour="cart-promos">
                  <h3 className="text-subhead font-display font-semibold text-ink inline-flex items-center gap-2 mb-1">
                    <Gift size={16} className="text-ink-subtle" />
                    Promo event
                  </h3>
                  <p className="text-caption text-ink-subtle mb-3">
                    Beli-gratis <b>otomatis</b> begitu jumlahnya cukup. Bundling dan diskon bertingkat
                    dinyalakan manual. Urutan: per baris → diskon bertingkat → diskon manual → PPN.
                  </p>
                  <ul className="space-y-1.5">
                    {activePromos.map((p) => {
                      const auto = p.kind === 'bogo';
                      const on = auto ? applied.some((a) => a.id === p.id) : co.promoIds.includes(p.id);
                      // What this promo alone would do to the cart — for the label.
                      const alone = applyPromos(lines, [toPromoDef(p)]);
                      const applicable = !alone.problem;
                      return (
                        <li key={p.id}>
                          <label
                            className={cn(
                              'flex items-start gap-2.5 px-3 py-2 rounded border text-small',
                              auto ? 'cursor-default' : 'cursor-pointer',
                              on ? 'border-primary bg-primary/5' : 'border-line hover:bg-canvas',
                              !applicable && !on && 'opacity-60',
                            )}
                          >
                            {auto ? (
                              <span
                                className="mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-primary/10 text-primary"
                                title="Berlaku otomatis saat jumlahnya cukup"
                              >
                                Auto
                              </span>
                            ) : (
                              <input
                                type="checkbox"
                                className="accent-primary mt-0.5"
                                checked={on}
                                onChange={() => co.togglePromo(p.id)}
                              />
                            )}
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-2">
                                {p.kind === 'double_discount' ? <Percent size={13} className="text-ink-subtle" /> : <Gift size={13} className="text-ink-subtle" />}
                                <span className="font-medium text-ink truncate">{p.name}</span>
                              </span>
                              <span className="block text-caption text-ink-subtle">
                                {describePromo(p)}
                                {applicable
                                  ? ` · hemat ${formatIDR(alone.promoDiscount)}`
                                  : ` · ${alone.problem}`}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                  {totals.promoProblem && (
                    <p role="alert" className="mt-2 text-caption text-danger inline-flex items-center gap-1.5">
                      <AlertTriangle size={13} /> {totals.promoProblem}
                    </p>
                  )}
                </Card>
              )}

              <Card data-tour="cart-totals">
                <div className="space-y-3">
                  <FulfillmentToggle value={co.fulfillment} onChange={co.setFulfillment} disabled={paymentOpen} />
                  <DiscountToggle
                    type={co.discountType}
                    value={co.discountValue}
                    subtotal={totals.subtotal - totals.promoDiscount}
                    onChange={(n) => co.setDiscount(n.type, n.value)}
                    onClear={() => co.setDiscount(null, 0)}
                    disabled={paymentOpen}
                  />
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-small font-medium text-ink">PPN {co.taxRate}%</p>
                    <Toggle checked={co.taxEnabled} onChange={co.setTaxEnabled} disabled={paymentOpen} aria-label="Toggle PPN" />
                  </div>
                  <div className="space-y-0.5 pt-2 border-t border-line text-body">
                    <Row label="Subtotal" value={formatIDR(totals.subtotal)} />
                    {totals.appliedPromos.map((a) => (
                      <Row key={a.promoId} label={a.name} value={`- ${formatIDR(a.amount)}`} tone="success" />
                    ))}
                    {totals.discountAmount > 0 && (
                      <Row
                        label={co.discountType === 'percent' ? `Diskon (${co.discountValue}%)` : 'Diskon (nominal)'}
                        value={`- ${formatIDR(totals.discountAmount)}`}
                        tone="success"
                      />
                    )}
                    {co.taxEnabled && <Row label={`PPN ${co.taxRate}%`} value={formatIDR(totals.taxAmount)} />}
                    <Row label="Grand total" value={formatIDR(totals.grandTotal)} bold />
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    leftIcon={<Receipt size={16} />}
                    disabled={!canCharge || paymentOpen}
                    onClick={() => { setError(null); setPaymentOpen(true); }}
                    data-tour="cart-charge"
                  >
                    {`Charge ${formatIDR(totals.grandTotal)}`}
                  </Button>
                  {!eventActive && <p className="text-caption text-danger">Event ini tidak aktif — transaksi tidak bisa dibuat.</p>}
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}

      {shift.eventId && (
        <Checkout
          open={paymentOpen}
          onClose={() => setPaymentOpen(false)}
          eventId={shift.eventId}
          customer={co.customer}
          totals={totals}
          discountType={co.discountType}
          discountValue={co.discountValue}
          taxEnabled={co.taxEnabled}
          taxRate={co.taxRate}
          fulfillment={co.fulfillment}
          promoIds={applied.map((p) => p.id)}
          onSuccess={handleSuccess}
          onError={(m) => { setError(m); }}
          cashierId={shift.cashierId}
          cashierName={shift.cashierName}
        />
      )}
      <CustomerForm open={customerFormOpen} onClose={() => setCustomerFormOpen(false)} onCreated={() => {}} />
    </div>
  );
}

function describePromo(p: EventPromo): string {
  if (p.kind === 'bundle') return `Bundling ${p.items.length} produk seharga ${formatIDR(Number(p.config.bundle_price ?? 0))}`;
  if (p.kind === 'bogo') {
    const rules = [...new Set(p.items.map((i) => `beli ${i.buyQty ?? p.config.buy_qty ?? '?'} gratis ${i.getQty ?? p.config.get_qty ?? '?'}`))];
    return rules.length === 1 ? rules[0][0].toUpperCase() + rules[0].slice(1) : `Beli-gratis per produk (${p.items.length} produk)`;
  }
  const steps = Array.isArray(p.config.steps) ? (p.config.steps as number[]) : [];
  const eff = 100 * (1 - steps.reduce((f, s) => f * (1 - Number(s) / 100), 1));
  return `Diskon ${steps.map((s) => `${s}%`).join(' + ')} = ${eff.toFixed(eff % 1 ? 1 : 0)}%`;
}

function LineCard({
  item,
  promoOff,
  onQty,
  onRemove,
  onLots,
  onAutoLots,
}: {
  item: CartItem;
  promoOff: number;
  onQty: (qty: number) => void;
  onRemove: () => void;
  onLots: (lots: { allocationId: string; qty: number }[]) => void;
  onAutoLots: () => void;
}) {
  const multi = item.lotOptions.length > 1;
  const assigned = assignedQty(item);
  const complete = assigned === item.qty;
  const qtyOf = (id: string) => item.lots.find((l) => l.allocationId === id)?.qty ?? 0;
  const setQty = (id: string, qty: number) => {
    const next = item.lots.filter((l) => l.allocationId !== id);
    if (qty > 0) next.push({ allocationId: id, qty });
    onLots(next);
  };

  return (
    <Card padding="md" className={cn(multi && !complete && 'border-warning/40')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-body font-medium text-ink truncate">{item.name}</p>
          <p className="text-caption text-ink-subtle">
            {formatIDR(item.price)} × {formatNumber(item.qty)}{item.unit ? ` ${item.unit}` : ''}
            {promoOff > 0 && <span className="text-success"> · promo −{formatIDR(promoOff)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="inline-flex items-center border border-line rounded">
            <button type="button" onClick={() => onQty(item.qty - 1)} className="w-9 h-9 text-ink-subtle hover:text-ink" aria-label="Kurangi">−</button>
            <span className="w-9 text-center text-small font-medium tabular-nums">{item.qty}</span>
            <button type="button" onClick={() => onQty(item.qty + 1)} disabled={item.qty >= item.maxStock} className="w-9 h-9 text-ink-subtle hover:text-ink disabled:opacity-40" aria-label="Tambah">+</button>
          </div>
          <p className="text-small font-semibold tabular-nums text-ink w-28 text-right">
            {formatIDR(item.price * item.qty - promoOff)}
          </p>
          <button type="button" onClick={onRemove} className="text-caption text-danger hover:underline">Hapus</button>
        </div>
      </div>

      {item.lotOptions.length === 1 && (item.lotOptions[0].lotNumber || item.lotOptions[0].expiryDate) && (
        <p className="mt-2 text-caption text-ink-subtle">
          Lot <span className="font-mono text-ink">{item.lotOptions[0].lotNumber ?? '—'}</span>
          {item.lotOptions[0].expiryDate && <> · ED {formatDate(item.lotOptions[0].expiryDate)}</>}
          <span className="ml-2 text-ink-subtle">sisa {formatNumber(item.lotOptions[0].remaining)}</span>
        </p>
      )}

      {multi && (
        <div className="mt-3 pt-3 border-t border-line">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-caption font-medium text-ink">
              Bagi per lot{' '}
              <span className="font-normal text-ink-subtle">· otomatis ED terdekat, ubah kalau barangnya beda</span>
              <button type="button" onClick={onAutoLots} className="ml-2 text-primary hover:underline font-normal">
                Atur otomatis
              </button>
            </p>
            <p className={cn('text-caption tabular-nums inline-flex items-center gap-1', complete ? 'text-success' : 'text-warning')}>
              {complete ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
              {assigned}/{item.qty}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-small">
              <thead className="text-caption text-ink-subtle">
                <tr>
                  <th className="text-left font-medium py-1">Lot</th>
                  <th className="text-left font-medium py-1">Expired</th>
                  <th className="text-right font-medium py-1">Sisa</th>
                  <th className="text-right font-medium py-1 w-24">Qty</th>
                </tr>
              </thead>
              <tbody>
                {item.lotOptions.map((o) => {
                  const q = qtyOf(o.allocationId);
                  const expired = !!o.expiryDate && o.expiryDate < Date.now();
                  return (
                    <tr key={o.allocationId} className="border-t border-line/60">
                      <td className="py-1 font-mono text-ink">{o.lotNumber ?? '—'}</td>
                      <td className={cn('py-1', expired ? 'text-danger font-medium' : 'text-ink-subtle')}>
                        {o.expiryDate ? formatDate(o.expiryDate) : '—'}
                      </td>
                      <td className="py-1 text-right tabular-nums text-ink-subtle">{formatNumber(o.remaining)}</td>
                      <td className="py-1 text-right">
                        <input
                          type="number"
                          min={0}
                          max={o.remaining}
                          value={q}
                          onChange={(e) => setQty(o.allocationId, Math.max(0, Math.min(o.remaining, Math.floor(Number(e.target.value) || 0))))}
                          className="w-20 h-9 px-2 text-right rounded border border-line bg-surface text-ink tabular-nums focus:outline-none focus:border-primary"
                          aria-label={`Qty lot ${o.lotNumber ?? ''}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

function Row({ label, value, tone = 'default', bold = false }: { label: string; value: string; tone?: 'default' | 'success'; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={cn(tone === 'success' ? 'text-success' : 'text-ink-subtle', bold && 'text-ink font-semibold')}>{label}</span>
      <span className={cn('tabular-nums', tone === 'success' ? 'text-success' : 'text-ink', bold && 'text-subhead font-display font-semibold')}>{value}</span>
    </div>
  );
}
